/**
 * Lettura della MRZ (Machine Readable Zone, ICAO 9303) dei documenti.
 *
 * Riceve le righe lette dall'OCR, le riconosce come TD1 (3 righe da 30:
 * carte d'identita', CIE, permessi di soggiorno), TD2 (2 righe da 36) o TD3
 * (2 righe da 44: passaporti) e ne estrae i dati.
 *
 * L'OCR confonde spesso caratteri simili (0 e O, 1 e I, 8 e B...). Ogni campo
 * della MRZ ha un tipo noto (solo cifre, solo lettere o misto) e quasi tutti
 * hanno una cifra di controllo: i campi numerici si correggono in base al
 * tipo, quelli misti (il numero del documento) provando le sostituzioni
 * finche' la cifra di controllo torna. Un campo e' "verificato" solo se la sua
 * cifra di controllo torna: e' la garanzia che la lettura e' giusta.
 */

const PESI = [7, 3, 1];

/**
 * Codici paese ICAO validi: ISO 3166 a tre lettere piu' i codici speciali
 * (D Germania, GBx britannici, UNx Nazioni Unite, XXx apolidi e rifugiati,
 * EUE Unione europea, RKS Kosovo...). Servono a scegliere la lettura giusta
 * della prima riga, che non ha cifre di controllo.
 */
const PAESI = new Set(
  "ABW AFG AGO AIA ALA ALB AND ARE ARG ARM ASM ATA ATF ATG AUS AUT AZE BDI BEL BEN" +
  "BES BFA BGD BGR BHR BHS BIH BLM BLR BLZ BMU BOL BRA BRB BRN BTN BVT BWA CAF CAN" +
  "CCK CHE CHL CHN CIV CMR COD COG COK COL COM CPV CRI CUB CUW CXR CYM CYP CZE D" +
  "DEU DJI DMA DNK DOM DZA ECU EGY ERI ESH ESP EST ETH EUE FIN FJI FLK FRA FRO FSM" +
  "GAB GBD GBN GBO GBP GBR GBS GEO GGY GHA GIB GIN GLP GMB GNB GNQ GRC GRD GRL GTM" +
  "GUF GUM GUY HKG HMD HND HRV HTI HUN IDN IMN IND IOT IRL IRN IRQ ISL ISR ITA JAM" +
  "JEY JOR JPN KAZ KEN KGZ KHM KIR KNA KOR KWT LAO LBN LBR LBY LCA LIE LKA LSO LTU" +
  "LUX LVA MAC MAF MAR MCO MDA MDG MDV MEX MHL MKD MLI MLT MMR MNE MNG MNP MOZ MRT" +
  "MSR MTQ MUS MWI MYS MYT NAM NCL NER NFK NGA NIC NIU NLD NOR NPL NRU NZL OMN PAK" +
  "PAN PCN PER PHL PLW PNG POL PRI PRK PRT PRY PSE PYF QAT REU RKS ROU RUS RWA SAU" +
  "SDN SEN SGP SGS SHN SJM SLB SLE SLV SMR SOM SPM SRB SSD STP SUR SVK SVN SWE SWZ" +
  "SXM SYC SYR TCA TCD TGO THA TJK TKL TKM TLS TON TTO TUN TUR TUV TWN TZA UGA UKR" +
  "UMI UNA UNK UNO URY USA UZB VAT VCT VEN VGB VIR VNM VUT WLF WSM XBA XCC XDC XES" +
  "XIM XKK XMP XOM XPO XXA XXB XXC XXX YEM ZAF ZMB ZWE ZZZ"
    .split(" ")
);

/** Valore di un carattere nel calcolo della cifra di controllo. */
function valore(c) {
  if (c >= "0" && c <= "9") {
    return c.charCodeAt(0) - 48;
  }
  if (c >= "A" && c <= "Z") {
    return c.charCodeAt(0) - 55;
  }
  return 0; // "<" e qualsiasi altro carattere
}

/** Cifra di controllo ICAO 9303 di una stringa. */
export function cifraControllo(s) {
  let tot = 0;
  for (let i = 0; i < s.length; i++) {
    tot += valore(s[i]) * PESI[i % 3];
  }
  return String(tot % 10);
}

// Confusioni tipiche dell'OCR, nei due sensi.
const A_CIFRA = { O: "0", Q: "0", D: "0", U: "0", I: "1", L: "1", T: "1", Z: "2", S: "5", B: "8", G: "6" };
const A_LETTERA = { 0: "O", 1: "I", 2: "Z", 5: "S", 8: "B", 6: "G" };

/** Forza un campo a sole cifre (date, cifre di controllo). Il "<" resta. */
function soloCifre(s) {
  return s.replace(/[A-Z]/g, (c) => A_CIFRA[c] || c);
}

/** Forza un campo a sole lettere (codici paese, nomi, sesso). */
function soloLettere(s) {
  return s.replace(/[0-9]/g, (c) => A_LETTERA[c] || c);
}

// Formati noti dei numeri dei documenti italiani: una lettura che li
// rispetta e' quasi certamente quella giusta.
const NUMERO_CIE = /^[A-Z]{2}[0-9]{5}[A-Z]{2}$/;
const NUMERO_PASSAPORTO = /^[A-Z]{2}[0-9]{7}$/;

/** Quante volte si passa da lettere a cifre o viceversa. */
function cambi(s) {
  let n = 0;
  for (let i = 1; i < s.length; i++) {
    const a = s[i - 1] >= "0" && s[i - 1] <= "9";
    const b = s[i] >= "0" && s[i] <= "9";
    if (s[i - 1] !== "<" && s[i] !== "<" && a !== b) {
      n++;
    }
  }
  return n;
}

/**
 * Corregge un campo misto (lettere e cifre) usando la cifra di controllo.
 *
 * La cifra di controllo e' un modulo 10: da sola non basta a scegliere tra
 * tutte le sostituzioni possibili, molte la soddisfano per caso. Tra quelle
 * che la soddisfano si preferisce chi rispetta un formato noto, poi chi ha
 * lettere e cifre in blocchi compatti (i numeri dei documenti sono fatti
 * cosi'), poi chi cambia meno caratteri. Restituisce il valore o null.
 *
 * @param {RegExp|null} formato forma attesa del valore, se nota
 */
function correggiConControllo(campo, cifra, formato) {
  if (!/^[0-9]$/.test(cifra)) {
    return null;
  }
  // Se la lettura torna gia' cosi' si tiene: le alternative che tornano anche
  // loro sono quasi sempre coincidenze del modulo 10. Fa eccezione un
  // documento di forma nota letto fuori forma.
  const pulitoLetto = campo.replace(/<+$/, "");
  if (cifraControllo(campo) === cifra && (!formato || formato.test(pulitoLetto))) {
    return campo;
  }
  const scambi = { 0: "O", O: "0", 1: "I", I: "1", 8: "B", B: "8", 5: "S", S: "5", 2: "Z", Z: "2", 6: "G", G: "6", D: "0", Q: "0" };
  const pos = [];
  for (let i = 0; i < campo.length; i++) {
    if (scambi[campo[i]]) {
      pos.push(i);
    }
  }
  if (pos.length > 12) {
    return cifraControllo(campo) === cifra ? campo : null;
  }
  let migliore = null;
  let puntiMigliore = Infinity;
  for (let mask = 0; mask < 1 << pos.length; mask++) {
    const c = campo.split("");
    let cambiati = 0;
    pos.forEach((p, k) => {
      if (mask & (1 << k)) {
        c[p] = scambi[c[p]];
        cambiati++;
      }
    });
    const prova = c.join("");
    if (cifraControllo(prova) !== cifra) {
      continue;
    }
    const pulito = prova.replace(/<+$/, "");
    const fuoriFormato = formato && !formato.test(pulito) ? 100 : 0;
    const punti = fuoriFormato + cambi(pulito) * 10 + cambiati;
    if (punti < puntiMigliore) {
      puntiMigliore = punti;
      migliore = prova;
    }
  }
  return migliore;
}

/** Pulisce una riga dell'OCR: maiuscole, niente spazi, solo caratteri MRZ. */
export function pulisciRiga(riga) {
  return String(riga)
    .toUpperCase()
    .replace(/[«‹([{]/g, "<") // "<" letti male
    .replace(/\s+/g, "")
    .replace(/[^A-Z0-9<]/g, "");
}

/**
 * Una riga (gia' pulita) sembra MRZ se e' lunga abbastanza e ha i riempitivi
 * "<" oppure molte cifre: la seconda riga di un passaporto puo' avere un solo
 * "<", o nessuno se i dati opzionali sono pieni.
 */
export function sembraMrz(riga, minimo = 26) {
  const minori = (riga.match(/</g) || []).length;
  const cifre = (riga.match(/[0-9]/g) || []).length;
  return riga.length >= minimo && (minori >= 2 || (minori >= 1 && cifre >= 6) || cifre >= 12);
}

/** Porta una riga alla lunghezza attesa: taglia o completa con "<". */
function normalizzaLunghezza(riga, len) {
  if (riga.length > len) {
    return riga.substring(0, len);
  }
  return riga + "<".repeat(len - riga.length);
}

/** Data AAMMGG della MRZ in AAAA-MM-GG. */
function data(aammgg, tipo) {
  if (!/^[0-9]{6}$/.test(aammgg)) {
    return null;
  }
  const aa = parseInt(aammgg.substring(0, 2), 10);
  const mm = parseInt(aammgg.substring(2, 4), 10);
  const gg = parseInt(aammgg.substring(4, 6), 10);
  if (mm < 1 || mm > 12 || gg < 1 || gg > 31) {
    return null;
  }
  const oggi = new Date().getFullYear() % 100;
  let secolo;
  if (tipo === "nascita") {
    // Nessuno nasce nel futuro: un anno maggiore di quello corrente e' del 1900.
    secolo = aa > oggi ? 1900 : 2000;
  } else {
    // Una scadenza e' al massimo una ventina d'anni avanti; un documento
    // scaduto da molto tempo puo' essere del secolo scorso.
    secolo = aa > oggi + 30 ? 1900 : 2000;
  }
  return secolo + aa + "-" + aammgg.substring(2, 4) + "-" + aammgg.substring(4, 6);
}

/** Cognome e nomi dal campo nome "COGNOME<<NOME<SECONDO". */
function nomi(campo) {
  // Una o due lettere sole dopo i riempitivi sono il bordo del documento
  // letto come lettera ("ERIKA<<<<<I"), non un altro nome.
  const pulito = soloLettere(campo)
    .replace(/<{3,}[A-Z]{1,2}<*$/, "")
    .replace(/<+$/, "");
  const [cognome, ...resto] = pulito.split("<<");
  const nome = resto.join(" ");
  const testo = (s) => s.replace(/<+/g, " ").replace(/\s+/g, " ").trim();
  return { cognome: testo(cognome || ""), nome: testo(nome) };
}

/** Codice paese ICAO a tre lettere (D per la Germania, completato con "<"). */
function paese(campo) {
  return soloLettere(campo).replace(/</g, "");
}

/** Sesso: M, F o X/< non specificato. */
function sesso(c) {
  const s = soloLettere(c);
  if (s === "M" || s === "F") {
    return s;
  }
  // "P" e "H" sono letture frequenti di "F" e "M".
  if (s === "P") {
    return "F";
  }
  if (s === "H" || s === "N") {
    return "M";
  }
  return "";
}

/**
 * Campo con la sua cifra di controllo: corregge e dice se e' verificato.
 *
 * @param {string} campo valore letto
 * @param {string} cifra cifra di controllo letta
 * @param {boolean} numerico se il campo e' di sole cifre
 * @param {RegExp|null} formato forma attesa, per i campi misti
 */
function verificato(campo, cifra, numerico, formato = null) {
  const c = soloCifre(cifra);
  if (numerico) {
    const v = soloCifre(campo);
    return { valore: v, ok: cifraControllo(v) === c };
  }
  const corretto = correggiConControllo(campo, c, formato);
  return corretto ? { valore: corretto, ok: true } : { valore: campo, ok: false };
}

/**
 * Numero del documento: nei TD1 se e' piu' lungo di 9 caratteri prosegue nei
 * dati opzionali, e la cifra di controllo sta dopo la parte che prosegue.
 */
function numeroLungo(campo9, cifra, opzionali, formato) {
  if (cifra === "<" && opzionali) {
    const fine = opzionali.indexOf("<");
    const parte = fine > 0 ? opzionali.substring(0, fine) : opzionali;
    if (parte.length >= 2) {
      const numero = campo9 + parte.substring(0, parte.length - 1);
      return verificato(numero, parte[parte.length - 1], false, formato);
    }
  }
  return null;
}

function td1(r) {
  const [l1, l2, l3] = r;
  const formato = paese(l1.substring(2, 5)) === "ITA" ? NUMERO_CIE : null;
  const documento = numeroLungo(l1.substring(5, 14), l1[14], l1.substring(15, 30), formato) || verificato(l1.substring(5, 14), l1[14], false, formato);
  const nascita = verificato(l2.substring(0, 6), l2[6], true);
  const scadenza = verificato(l2.substring(8, 14), l2[14], true);
  // Composito: numero+cifra+opzionali (riga 1, 5-29), nascita+cifra,
  // scadenza+cifra, opzionali (riga 2, 18-28).
  const campoNumero = documento.valore.length === 9 ? documento.valore : l1.substring(5, 14);
  const composita = campoNumero + l1.substring(14, 30) + nascita.valore + l2[6] + scadenza.valore + l2[14] + l2.substring(18, 29);
  return {
    formato: "TD1",
    tipo: soloLettere(l1.substring(0, 2)),
    emittente: paese(l1.substring(2, 5)),
    numero: documento.valore.replace(/<+$/, ""),
    nascita,
    sesso: sesso(l2[7]),
    scadenza,
    nazionalita: paese(l2.substring(15, 18)),
    opzionali: (l1.substring(15, 30) + l2.substring(18, 29)).replace(/<+/g, " ").trim(),
    ...nomi(l3),
    controlli: {
      numero: documento.ok,
      nascita: nascita.ok,
      scadenza: scadenza.ok,
      composito: cifraControllo(composita) === soloCifre(l2[29])
    }
  };
}

/** TD2 e TD3 hanno la stessa struttura, cambia solo la lunghezza. */
function td23(r, len) {
  const [l1, l2] = r;
  const td3 = len === 44;
  const formato = td3 && paese(l1.substring(2, 5)) === "ITA" ? NUMERO_PASSAPORTO : null;
  const documento = verificato(l2.substring(0, 9), l2[9], false, formato);
  const nascita = verificato(l2.substring(13, 19), l2[19], true);
  const scadenza = verificato(l2.substring(21, 27), l2[27], true);
  const fineOpz = td3 ? 42 : 35;
  const opzionali = l2.substring(28, fineOpz);
  const composita = documento.valore + l2[9] + nascita.valore + l2[19] + scadenza.valore + l2[27] + opzionali + (td3 ? l2[42] : "");
  const ris = {
    formato: td3 ? "TD3" : "TD2",
    tipo: soloLettere(l1.substring(0, 2)),
    emittente: paese(l1.substring(2, 5)),
    numero: documento.valore.replace(/<+$/, ""),
    nascita,
    sesso: sesso(l2[20]),
    scadenza,
    nazionalita: paese(l2.substring(10, 13)),
    opzionali: opzionali.replace(/<+/g, " ").trim(),
    ...nomi(l1.substring(5, len)),
    controlli: {
      numero: documento.ok,
      nascita: nascita.ok,
      scadenza: scadenza.ok,
      composito: cifraControllo(composita) === soloCifre(l2[len - 1])
    }
  };
  // I visti (V) non hanno la cifra composita.
  if (ris.tipo[0] === "V") {
    ris.controlli.composito = true;
  }
  return ris;
}

/** Quanto si somiglia una riga letta alla lunghezza attesa (0 = identica). */
function distanza(righe, len) {
  return righe.reduce((tot, r) => tot + Math.abs(r.length - len), 0);
}

/**
 * Tutti i modi di portare le righe alla lunghezza `len`.
 *
 * Una riga piu' lunga puo' avere caratteri spuri all'inizio (il bordo del
 * documento letto come "I", un pezzo di fondino): oltre a tagliare la coda si
 * prova a scartare da 1 a 6 caratteri in testa.
 */
function allineamenti(righe, len) {
  let combinazioni = [[]];
  for (const r of righe) {
    const extra = Math.min(6, Math.max(0, r.length - len));
    const varianti = [];
    for (let s = 0; s <= extra; s++) {
      varianti.push(normalizzaLunghezza(r.substring(s), len));
    }
    combinazioni = combinazioni.flatMap((c) => varianti.map((v) => [...c, v]));
  }
  return combinazioni;
}

/**
 * Cerca una MRZ tra le righe lette dall'OCR e ne estrae i dati.
 *
 * @param {string[]|string} testo righe dell'OCR (o testo con a capo)
 * @returns {object|null} dati letti, con `controlli` e `valida`, o null
 */
export function leggiMrz(testo) {
  const righe = (Array.isArray(testo) ? testo : String(testo).split(/\r?\n/)).map(pulisciRiga).filter((r) => sembraMrz(r));
  if (!righe.length) {
    return null;
  }
  const candidati = [];
  // Ogni gruppo di righe consecutive puo' essere una MRZ: TD1 ne ha 3, TD2 e TD3 2.
  for (let i = 0; i < righe.length; i++) {
    const tre = righe.slice(i, i + 3);
    if (tre.length === 3 && distanza(tre, 30) <= 12) {
      // La terza riga (i nomi) non ha cifre di controllo per scegliere tra
      // gli allineamenti: se e' piu' lunga di 30, l'eccesso e' il bordo della
      // carta letto come lettera. I nomi finiscono con i riempitivi "<": se
      // l'ultimo carattere non e' "<" l'intruso e' in coda ("...<<<I"),
      // altrimenti in testa ("BMUSTERMANN<<...").
      while (tre[2].length > 30) {
        tre[2] = tre[2].endsWith("<") ? tre[2].substring(1) : tre[2].slice(0, -1);
      }
      allineamenti(tre, 30).forEach((a) => candidati.push(td1(a)));
    }
    const due = righe.slice(i, i + 2);
    if (due.length === 2) {
      if (distanza(due, 44) <= 10) {
        allineamenti(due, 44).forEach((a) => candidati.push(td23(a, 44)));
      }
      if (distanza(due, 36) <= 10) {
        allineamenti(due, 36).forEach((a) => candidati.push(td23(a, 36)));
      }
    }
  }
  if (!candidati.length) {
    return null;
  }
  // Lettere iniziali ammesse per formato: TD3 solo passaporti e visti.
  const TIPI = { TD1: "IAC", TD2: "IACPV", TD3: "PV" };
  // Vince il candidato con piu' controlli superati; a parita' quello piu'
  // plausibile: la prima riga dei TD2 e TD3 non ha cifre di controllo e un
  // carattere spurio in testa la sposterebbe tutta.
  const plausibilita = (c) => (PAESI.has(c.emittente) ? 1 : 0) + (PAESI.has(c.nazionalita) ? 1 : 0) + (TIPI[c.formato].includes(c.tipo[0]) ? 1 : 0) + (c.emittente === c.nazionalita ? 1 : 0);
  const punti = (c) => Object.values(c.controlli).filter(Boolean).length + plausibilita(c) / 10;
  candidati.sort((a, b) => punti(b) - punti(a));
  const migliore = candidati[0];
  return {
    ...migliore,
    dataNascita: data(migliore.nascita.valore, "nascita"),
    dataScadenza: data(migliore.scadenza.valore, "scadenza"),
    nascita: undefined,
    scadenza: undefined,
    valida: Object.values(migliore.controlli).every(Boolean)
  };
}

export default { leggiMrz, cifraControllo, pulisciRiga, sembraMrz };
